import { useEffect, useState, useRef } from 'react';
import type { ComponentType } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import clsx from 'clsx';
import { RaceSelectionSettings, PaymentSettings, AgreementSettings, ShirtSelectionSettings, MerchandiseSettings, ShippingSettings } from '../components/settings';
import JsonSettingsEditor from '../components/JsonSettingsEditor';
import {
  card, cardBody, input, textarea, label, hint,
  btnPrimary, btnSecondary, btnDanger, btnSm, iconBtn, iconBtnDanger, iconBtnGhost,
  StatusChip, Badge, Alert, EmptyState, Toggle,
} from '../components/ui';
import {
  IconChevronLeft, IconEye, IconCheck, IconPlus, IconTrash, IconX, IconGrip, IconCode, IconUpload,
  IconLayers, IconPointer, IconSettings, IconSpinner,
  IconType, IconMail, IconPhone, IconIdCard, IconFile, IconHash, IconCalendar, IconList, IconRadio,
  IconCheckSquare, IconAlignLeft, IconImage, IconCamera, IconPalette,
} from '../components/Icons';

type IconComponent = ComponentType<{ size?: number; className?: string }>;

const fieldTypes: { type: string; Icon: IconComponent; label: string }[] = [
  { type: 'text', Icon: IconType, label: '文字' },
  { type: 'email', Icon: IconMail, label: 'Email' },
  { type: 'tel', Icon: IconPhone, label: '電話' },
  { type: 'taiwan_id', Icon: IconIdCard, label: '身分證' },
  { type: 'resident_cert', Icon: IconFile, label: '居留證' },
  { type: 'number', Icon: IconHash, label: '數字' },
  { type: 'date', Icon: IconCalendar, label: '日期' },
  { type: 'select', Icon: IconList, label: '下拉選單' },
  { type: 'radio', Icon: IconRadio, label: '單選' },
  { type: 'checkbox', Icon: IconCheckSquare, label: '勾選' },
  { type: 'textarea', Icon: IconAlignLeft, label: '多行文字' },
  { type: 'image_display', Icon: IconImage, label: '圖片顯示' },
  { type: 'image_upload', Icon: IconCamera, label: '圖片上傳' },
  { type: 'image_radio', Icon: IconPalette, label: '圖片選擇' },
];

// `icon` here is attendee-facing data rendered by the LIFF app; the admin UI no longer displays it.
const defaultModules = [
  { type: 'agreement', name: '報名須知', nameEn: 'Agreement', icon: '📋' },
  { type: 'contact', name: '聯絡資訊', nameEn: 'Contact Info', icon: '📱' },
  { type: 'participant', name: '參賽者資料', nameEn: 'Participant Info', icon: '👤' },
  { type: 'race_selection', name: '組別選擇', nameEn: 'Race Selection', icon: '🏃' },
  { type: 'shirt_selection', name: '紀念衣選擇', nameEn: 'Shirt Selection', icon: '👕' },
  { type: 'merchandise', name: '商品加購', nameEn: 'Merchandise', icon: '🛍️' },
  { type: 'payment', name: '付款資訊', nameEn: 'Payment Info', icon: '💳' },
  { type: 'shipping', name: '寄送資訊', nameEn: 'Shipping Info', icon: '📦' },
  { type: 'notes', name: '備註', nameEn: 'Notes', icon: '📝' },
  { type: 'custom', name: '自訂模組', nameEn: 'Custom', icon: '⚙️' },
];

interface Theme {
  id: string;
  name: string;
  nameEn?: string;
  primaryColor: string;
  primaryDark: string;
  primaryLight?: string;
  secondaryColor?: string;
  accentColor?: string;
  heroStyle?: string;
  heroGradient?: string;
  heroPattern?: string;
  darkMode?: boolean;
  cardStyle?: string;
  borderRadius?: string;
  isSystem?: boolean;
}

interface Module {
  id: string;
  type: string;
  name: string;
  nameEn?: string;
  icon?: string;
  headerStyle?: string;
  headerColor?: string;
  sortOrder: number;
  fields: any[];
  settings: any;
  isNew?: boolean;
  isDeleted?: boolean;
}

interface EventData {
  id?: string;
  title: string;
  titleEn?: string;
  subtitle?: string;
  description?: string;
  bannerImageUrl?: string;
  thumbnailImageUrl?: string;
  eventDate?: string;
  registrationDeadline?: string;
  lineGroupUrl?: string;
  lineGroupQrCodeUrl?: string;
  status: string;
  themeId?: string;
  theme: {
    primaryColor: string;
    primaryDark: string;
    heroStyle: string;
  };
}

// Helper function to get module item count based on module type
function getModuleItemInfo(module: Module): { count: number; label: string } {
  const settings = module.settings || {};
  const fields = module.fields || [];

  switch (module.type) {
    case 'race_selection':
      const categoryCount = settings.categories?.length || 0;
      return { count: categoryCount, label: categoryCount === 1 ? '組別' : '組別' };
    case 'merchandise':
      const itemCount = settings.items?.length || 0;
      return { count: itemCount, label: itemCount === 1 ? '商品' : '商品' };
    case 'shirt_selection':
      const typeCount = settings.typeOptions?.length || 0;
      const sizeCount = (settings.adultSizes?.length || 0) + (settings.kidsSizes?.length || 0);
      if (typeCount > 0 || sizeCount > 0) {
        return { count: typeCount + sizeCount, label: '選項' };
      }
      return { count: fields.length, label: '欄位' };
    default:
      return { count: fields.length, label: '欄位' };
  }
}

// Helper function to check if module has configured settings
function hasConfiguredSettings(module: Module): boolean {
  const settings = module.settings || {};

  // Check based on module type
  switch (module.type) {
    case 'race_selection':
      return !!(
        (settings.categories && settings.categories.length > 0) ||
        (settings.shippingOptions && settings.shippingOptions.length > 0) ||
        settings.displayStyle ||
        settings.showOriginalPrice ||
        settings.discountLabel
      );
    case 'payment':
      return !!(
        settings.bankName ||
        settings.bankCode ||
        settings.accountNumber ||
        settings.accountName
      );
    case 'agreement':
      return !!(
        settings.lineGroupUrl ||
        settings.qrCodeImageUrl ||
        settings.warningText ||
        settings.agreementText
      );
    case 'shirt_selection':
      return !!(
        settings.showTypeSelection ||
        (settings.typeOptions && settings.typeOptions.length > 0) ||
        settings.showSizeChart ||
        (settings.adultSizes && settings.adultSizes.length > 0) ||
        (settings.kidsSizes && settings.kidsSizes.length > 0)
      );
    case 'merchandise':
      return !!(
        settings.title ||
        settings.subtitle ||
        (settings.items && settings.items.length > 0)
      );
    case 'shipping':
      return !!(
        settings.pickupLocation ||
        settings.pickupLocationUrl ||
        settings.pickupNote
      );
    default:
      // For other modules, check if settings object has any keys with values
      return Object.values(settings).some(v =>
        v !== undefined && v !== null && v !== '' &&
        !(Array.isArray(v) && v.length === 0)
      );
  }
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://group-booking-api.georgec520w.workers.dev';
const LIFF_BASE = 'https://group-booking-liff.pages.dev';

/** URL input + upload button + optional preview, used for banner / thumbnail / QR code. */
function ImageField({ id, labelText, value, placeholder, helpText, onChange, onPick, uploading, inputRef, onUpload, preview, previewClass }: {
  id: string;
  labelText: string;
  value: string;
  placeholder: string;
  helpText: string;
  onChange: (v: string) => void;
  onPick: () => void;
  uploading: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  preview?: string;
  previewClass: string;
}) {
  return (
    <div>
      <label htmlFor={id} className={label}>{labelText}</label>
      <div className="flex gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex gap-2">
            <input
              id={id}
              type="url"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className={input}
              placeholder={placeholder}
            />
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onUpload} className="hidden" />
            <button type="button" onClick={onPick} disabled={uploading} className={clsx(btnSecondary, 'shrink-0')}>
              {uploading ? <IconSpinner size={16} /> : <IconUpload size={16} />}
              {uploading ? '上傳中' : '上傳'}
            </button>
          </div>
          <p className={hint}>{helpText}</p>
        </div>
        {preview && (
          <div className={clsx('border border-line rounded-md overflow-hidden bg-ground shrink-0 flex items-center justify-center', previewClass)}>
            <img
              src={preview}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function EventEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploadingQr, setUploadingQr] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [activeTab, setActiveTab] = useState<'fields' | 'properties' | 'settings'>('fields');
  const [selectedField, setSelectedField] = useState<any>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);

  // On phones and tablets the side panel sits below the canvas; bring it into view
  // when the user picks a field or opens module settings.
  useEffect(() => {
    if (activeTab === 'fields') return;
    if (window.matchMedia('(min-width: 1024px)').matches) return;
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeTab, selectedField?.field?.id]);
  const qrCodeInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);

  const [event, setEvent] = useState<EventData>({
    title: '',
    titleEn: '',
    subtitle: '',
    description: '',
    bannerImageUrl: '',
    thumbnailImageUrl: '',
    eventDate: '',
    registrationDeadline: '',
    lineGroupUrl: '',
    lineGroupQrCodeUrl: '',
    status: 'draft',
    theme: {
      primaryColor: '#6366F1',
      primaryDark: '#4F46E5',
      heroStyle: 'gradient',
    },
  });

  const [modules, setModules] = useState<Module[]>([]);
  const [originalModules, setOriginalModules] = useState<Module[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);

  useEffect(() => {
    loadThemes();
    if (id) {
      loadEvent(id);
    }
  }, [id]);

  async function loadThemes() {
    try {
      const res = await api.getThemes();
      if (res.success && res.data) {
        // API returns { system: [...], custom: [...] }
        const allThemes = [
          ...(res.data.system || []),
          ...(res.data.custom || []),
        ];
        setThemes(allThemes);
      }
    } catch (error) {
      console.error('Failed to load themes:', error);
    }
  }

  async function loadEvent(eventId: string) {
    try {
      const res = await api.getEvent(eventId);
      if (res.success && res.data) {
        const data = res.data;

        // Map API response to event data
        setEvent({
          id: data.id,
          title: data.title || '',
          titleEn: data.titleEn || '',
          subtitle: data.subtitle || '',
          description: data.description || '',
          bannerImageUrl: data.bannerImageUrl || '',
          thumbnailImageUrl: data.thumbnailImageUrl || '',
          eventDate: data.eventDate || '',
          registrationDeadline: data.registrationDeadline?.split('T')[0] || '',
          lineGroupUrl: data.lineGroupUrl || '',
          lineGroupQrCodeUrl: data.lineGroupQrCodeUrl || '',
          status: data.status || 'draft',
          themeId: data.themeId || data.theme?.id || '',
          theme: {
            primaryColor: data.theme?.primaryColor || data.themePrimaryColor || '#6366F1',
            primaryDark: data.theme?.primaryDark || '#4F46E5',
            heroStyle: data.theme?.heroStyle || 'gradient',
          },
        });

        // Load modules from API response
        const loadedModules: Module[] = (data.modules || []).map((m: any, idx: number) => ({
          id: m.id,
          type: m.type,
          name: m.name,
          nameEn: m.nameEn,
          icon: m.icon,
          headerStyle: m.headerStyle,
          headerColor: m.headerColor,
          sortOrder: m.sortOrder ?? idx,
          fields: m.fields || [],
          settings: m.settings || {},
        }));

        setModules(loadedModules);
        setOriginalModules(JSON.parse(JSON.stringify(loadedModules)));
      }
    } catch (error) {
      console.error('Failed to load event:', error);
      alert('載入活動失敗');
    } finally {
      setLoading(false);
    }
  }

  async function handleQrCodeUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingQr(true);
    try {
      const res = await api.uploadQrCode(file, id);
      if (res.success && res.data?.url) {
        setEvent({ ...event, lineGroupQrCodeUrl: `${API_BASE}${res.data.url}` });
      } else {
        alert('上傳失敗');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('上傳失敗');
    } finally {
      setUploadingQr(false);
      if (qrCodeInputRef.current) {
        qrCodeInputRef.current.value = '';
      }
    }
  }

  async function handleBannerUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingBanner(true);
    try {
      const res = await api.uploadBanner(file, id);
      if (res.success && res.data?.url) {
        setEvent({ ...event, bannerImageUrl: `${API_BASE}${res.data.url}` });
      } else {
        alert('上傳失敗');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('上傳失敗');
    } finally {
      setUploadingBanner(false);
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
    }
  }

  async function handleThumbnailUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingThumbnail(true);
    try {
      const res = await api.uploadBanner(file, id);
      if (res.success && res.data?.url) {
        setEvent({ ...event, thumbnailImageUrl: `${API_BASE}${res.data.url}` });
      } else {
        alert('上傳失敗');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('上傳失敗');
    } finally {
      setUploadingThumbnail(false);
      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      }
    }
  }

  async function handleSave(publish = false) {
    if (!event.title) {
      alert('請輸入活動名稱');
      return;
    }

    setSaving(true);
    try {
      let eventId = id;

      // Create or update event basic info
      const eventData = {
        title: event.title,
        titleEn: event.titleEn,
        subtitle: event.subtitle,
        description: event.description,
        bannerImageUrl: event.bannerImageUrl,
        thumbnailImageUrl: event.thumbnailImageUrl,
        eventDate: event.eventDate,
        registrationDeadline: event.registrationDeadline,
        lineGroupUrl: event.lineGroupUrl,
        lineGroupQrCodeUrl: event.lineGroupQrCodeUrl,
        status: publish ? 'published' : event.status,
        themeId: event.themeId || null,
      };

      if (isNew) {
        const res = await api.createEvent(eventData);
        if (res.success && res.data) {
          eventId = res.data.id;
        } else {
          throw new Error('Failed to create event');
        }
      } else {
        await api.updateEvent(id!, eventData);
      }

      // Save modules
      if (eventId) {
        // Find deleted modules
        for (const orig of originalModules) {
          const still = modules.find(m => m.id === orig.id);
          if (!still) {
            await api.deleteModule(eventId, orig.id);
          }
        }

        // Add/update modules
        for (let i = 0; i < modules.length; i++) {
          const mod = modules[i];
          const moduleData = {
            type: mod.type,
            name: mod.name,
            nameEn: mod.nameEn,
            icon: mod.icon,
            headerStyle: mod.headerStyle,
            headerColor: mod.headerColor,
            sortOrder: i,
            fields: mod.fields,
            settings: mod.settings,
          };

          if (mod.isNew) {
            await api.addModule(eventId, moduleData);
          } else {
            const orig = originalModules.find(o => o.id === mod.id);
            if (orig && JSON.stringify(orig) !== JSON.stringify(mod)) {
              await api.updateModule(eventId, mod.id, moduleData);
            }
          }
        }
      }

      alert(isNew ? '活動建立成功！' : '活動更新成功！');

      if (isNew) {
        navigate('/events');
      } else {
        // Reload to get fresh data
        loadEvent(eventId!);
      }
    } catch (error) {
      console.error('Failed to save event:', error);
      alert('儲存失敗: ' + (error as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function addModule(moduleTemplate: typeof defaultModules[0]) {
    const newModule: Module = {
      id: `new_${Date.now()}`,
      type: moduleTemplate.type,
      name: moduleTemplate.name,
      nameEn: moduleTemplate.nameEn,
      icon: moduleTemplate.icon,
      sortOrder: modules.length,
      fields: [],
      settings: {},
      isNew: true,
    };
    setModules([...modules, newModule]);
    setSelectedModuleId(newModule.id);
  }

  function removeModule(moduleId: string) {
    setModules(modules.filter((m) => m.id !== moduleId));
    if (selectedModuleId === moduleId) {
      setSelectedModuleId(null);
    }
    if (selectedField?.moduleId === moduleId) {
      setSelectedField(null);
    }
  }

  function updateModule(moduleId: string, updates: Partial<Module>) {
    setModules(modules.map((m) =>
      m.id === moduleId ? { ...m, ...updates } : m
    ));
  }

  function addFieldToModule(moduleId: string, fieldType: string) {
    // Generate meaningful default ID based on field type
    const defaultIds: Record<string, { id: string; label: string; labelEn: string }> = {
      text: { id: 'text_field', label: '文字欄位', labelEn: 'Text Field' },
      email: { id: 'email', label: 'Email', labelEn: 'Email' },
      tel: { id: 'phone', label: '電話', labelEn: 'Phone' },
      taiwan_id: { id: 'taiwan_id', label: '身分證字號', labelEn: 'Taiwan ID' },
      resident_cert: { id: 'resident_cert', label: '居留證號碼', labelEn: 'Resident Certificate' },
      number: { id: 'number_field', label: '數字欄位', labelEn: 'Number Field' },
      date: { id: 'date_field', label: '日期', labelEn: 'Date' },
      select: { id: 'select_field', label: '選擇欄位', labelEn: 'Select Field' },
      radio: { id: 'radio_field', label: '單選欄位', labelEn: 'Radio Field' },
      checkbox: { id: 'checkbox_field', label: '勾選項目', labelEn: 'Checkbox' },
      textarea: { id: 'notes', label: '備註', labelEn: 'Notes' },
      image_display: { id: 'image_display', label: '圖片', labelEn: 'Image' },
      image_upload: { id: 'image_upload', label: '上傳圖片', labelEn: 'Upload Image' },
      image_radio: { id: 'image_select', label: '圖片選擇', labelEn: 'Image Select' },
    };

    const defaults = defaultIds[fieldType] || { id: `field_${Date.now()}`, label: '新欄位', labelEn: '' };
    // Add timestamp suffix to avoid duplicate IDs
    const uniqueId = `${defaults.id}_${Date.now().toString(36)}`;

    const field = {
      id: uniqueId,
      type: fieldType,
      label: defaults.label,
      labelEn: defaults.labelEn,
      placeholder: '',
      required: false,
      options: ['select', 'radio', 'image_radio'].includes(fieldType)
        ? [{ value: 'opt1', label: '選項 1' }]
        : undefined,
    };

    setModules(modules.map((m) =>
      m.id === moduleId
        ? { ...m, fields: [...(m.fields || []), field] }
        : m
    ));

    setSelectedField({ moduleId, field });
    setActiveTab('properties');
  }

  function updateField(moduleId: string, fieldId: string, updates: any) {
    setModules(modules.map((m) =>
      m.id === moduleId
        ? {
            ...m,
            fields: (m.fields || []).map((f: any) =>
              f.id === fieldId ? { ...f, ...updates } : f
            ),
          }
        : m
    ));

    if (selectedField?.field?.id === fieldId) {
      setSelectedField({
        ...selectedField,
        field: { ...selectedField.field, ...updates },
      });
    }
  }

  function removeField(moduleId: string, fieldId: string) {
    setModules(modules.map((m) =>
      m.id === moduleId
        ? { ...m, fields: (m.fields || []).filter((f: any) => f.id !== fieldId) }
        : m
    ));

    if (selectedField?.field?.id === fieldId) {
      setSelectedField(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] lg:h-screen gap-2 text-sm text-muted">
        <IconSpinner size={18} />
        載入中
      </div>
    );
  }

  const fieldTypeOf = (type: string) => fieldTypes.find((f) => f.type === type);
  const panelTabs: { key: typeof activeTab; label: string }[] = [
    { key: 'fields', label: '新增欄位' },
    { key: 'properties', label: '欄位屬性' },
    { key: 'settings', label: '模組設定' },
  ];

  return (
    <div className="flex flex-col lg:h-screen bg-ground">
      {/* Top Bar */}
      <header className="sticky top-14 lg:static z-30 bg-white border-b border-line px-3 sm:px-6 h-14 lg:h-16 flex items-center justify-between gap-2 sm:gap-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/events" aria-label="返回活動列表" title="返回活動列表" className={iconBtn}>
            <IconChevronLeft size={16} />
          </Link>
          <h1 className="text-[15px] sm:text-base font-semibold truncate">
            {isNew ? '新增活動' : event.title || '編輯活動'}
          </h1>
          {!isNew && <StatusChip status={event.status} className="hidden sm:inline-flex" />}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isNew && (
            <button
              type="button"
              onClick={() => window.open(`${LIFF_BASE}/event/${id}?preview=true`, '_blank', 'width=414,height=896')}
              className={clsx(btnSecondary, '!px-2.5 sm:!px-4')}
              aria-label="預覽"
              title="預覽"
            >
              <IconEye size={16} />
              <span className="hidden sm:inline">預覽</span>
            </button>
          )}
          <button type="button" onClick={() => handleSave(false)} disabled={saving} className={clsx(btnSecondary, '!px-3 sm:!px-4')}>
            {saving ? '儲存中' : <><span className="sm:hidden">草稿</span><span className="hidden sm:inline">儲存草稿</span></>}
          </button>
          <button type="button" onClick={() => handleSave(true)} disabled={saving} className={clsx(btnPrimary, '!px-3 sm:!px-4')}>
            {saving ? <IconSpinner size={16} /> : <IconCheck size={16} />}
            <span className="sm:hidden">{event.status === 'published' ? '更新' : '發布'}</span>
            <span className="hidden sm:inline">{event.status === 'published' ? '更新發布' : '發布活動'}</span>
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex flex-col lg:flex-row lg:flex-1 lg:overflow-hidden">
        {/* Form Canvas */}
        <div className="flex-1 min-w-0 p-4 sm:p-6 lg:overflow-y-auto flex flex-col gap-5 sm:gap-6">
          {/* Basic Info */}
          <section className={card}>
            <div className="px-4 sm:px-6 py-4 border-b border-line-soft">
              <h2 className="text-base font-semibold leading-none tracking-tight">活動基本資訊</h2>
            </div>
            <div className={clsx(cardBody, 'grid grid-cols-1 lg:grid-cols-2 gap-5')}>
              <div>
                <label htmlFor="title" className={label}>活動名稱 <span className="text-bad">*</span></label>
                <input
                  id="title"
                  type="text"
                  value={event.title}
                  onChange={(e) => setEvent({ ...event, title: e.target.value })}
                  className={input}
                  placeholder="例：2026 台新女子半馬團報"
                />
              </div>
              <div>
                <label htmlFor="titleEn" className={label}>英文名稱</label>
                <input
                  id="titleEn"
                  type="text"
                  value={event.titleEn || ''}
                  onChange={(e) => setEvent({ ...event, titleEn: e.target.value })}
                  className={input}
                  placeholder="English title"
                />
              </div>
              <div className="lg:col-span-2">
                <label htmlFor="description" className={label}>活動說明</label>
                <textarea
                  id="description"
                  value={event.description || ''}
                  onChange={(e) => setEvent({ ...event, description: e.target.value })}
                  className={textarea}
                  placeholder="活動說明、注意事項、報名規則"
                  rows={6}
                />
                <p className={hint}>顯示在報名表單頂部。</p>
              </div>
              <div className="lg:col-span-2">
                <ImageField
                  id="bannerImageUrl"
                  labelText="Banner 圖片"
                  value={event.bannerImageUrl || ''}
                  placeholder="https://example.com/banner.jpg"
                  helpText="貼上圖片網址或上傳。建議 1200 × 400。"
                  onChange={(v) => setEvent({ ...event, bannerImageUrl: v })}
                  onPick={() => bannerInputRef.current?.click()}
                  uploading={uploadingBanner}
                  inputRef={bannerInputRef}
                  onUpload={handleBannerUpload}
                  preview={event.bannerImageUrl || undefined}
                  previewClass="w-32 h-20"
                />
              </div>
              <div className="lg:col-span-2">
                <ImageField
                  id="thumbnailImageUrl"
                  labelText="縮圖（活動卡片用）"
                  value={event.thumbnailImageUrl || ''}
                  placeholder="https://example.com/thumbnail.jpg"
                  helpText="用於活動列表卡片。建議 400 × 400 正方形。"
                  onChange={(v) => setEvent({ ...event, thumbnailImageUrl: v })}
                  onPick={() => thumbnailInputRef.current?.click()}
                  uploading={uploadingThumbnail}
                  inputRef={thumbnailInputRef}
                  onUpload={handleThumbnailUpload}
                  preview={event.thumbnailImageUrl || undefined}
                  previewClass="w-20 h-20"
                />
              </div>
              <div>
                <label htmlFor="eventDate" className={label}>活動日期</label>
                <input
                  id="eventDate"
                  type="date"
                  value={event.eventDate || ''}
                  onChange={(e) => setEvent({ ...event, eventDate: e.target.value })}
                  className={input}
                />
              </div>
              <div>
                <label htmlFor="registrationDeadline" className={label}>報名截止</label>
                <input
                  id="registrationDeadline"
                  type="date"
                  value={event.registrationDeadline || ''}
                  onChange={(e) => setEvent({ ...event, registrationDeadline: e.target.value })}
                  className={input}
                />
              </div>
              <div className="lg:col-span-2">
                <label htmlFor="lineGroupUrl" className={label}>LINE 群組連結</label>
                <input
                  id="lineGroupUrl"
                  type="url"
                  value={event.lineGroupUrl || ''}
                  onChange={(e) => setEvent({ ...event, lineGroupUrl: e.target.value })}
                  className={input}
                  placeholder="https://line.me/ti/g/..."
                />
                <p className={hint}>報名者按「加入群組」時會開啟此連結。</p>
              </div>
              <div className="lg:col-span-2 flex flex-col gap-3">
                <ImageField
                  id="lineGroupQrCodeUrl"
                  labelText="LINE 群組 QR Code"
                  value={event.lineGroupQrCodeUrl || ''}
                  placeholder="https://example.com/qrcode.png"
                  helpText="貼上圖片網址或上傳。建議 300 × 300。留空時會從上方連結自動產生。"
                  onChange={(v) => setEvent({ ...event, lineGroupQrCodeUrl: v })}
                  onPick={() => qrCodeInputRef.current?.click()}
                  uploading={uploadingQr}
                  inputRef={qrCodeInputRef}
                  onUpload={handleQrCodeUpload}
                  preview={
                    event.lineGroupQrCodeUrl ||
                    (event.lineGroupUrl
                      ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(event.lineGroupUrl)}`
                      : undefined)
                  }
                  previewClass="w-24 h-24 bg-white"
                />
                <Alert tone="info">若不想公開群組連結，只上傳 QR Code 圖片、留空上方連結即可。</Alert>
              </div>

              {/* Theme Selector */}
              <div className="lg:col-span-2">
                <span className={label}>主題風格</span>
                <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
                  {themes.map((theme) => {
                    const isSelected = event.themeId === theme.id;
                    return (
                      <button
                        key={theme.id}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => {
                          setEvent({
                            ...event,
                            themeId: theme.id,
                            theme: {
                              primaryColor: theme.primaryColor,
                              primaryDark: theme.primaryDark,
                              heroStyle: theme.heroStyle || 'gradient',
                            },
                          });
                        }}
                        className={clsx(
                          'p-3 rounded-lg border text-left transition-colors',
                          isSelected
                            ? 'border-primary bg-primary-light/60 ring-[3px] ring-primary/20'
                            : 'border-line hover:bg-ground'
                        )}
                      >
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="w-6 h-6 rounded-full border border-black/10" style={{ backgroundColor: theme.primaryColor }} />
                          <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: theme.primaryDark }} />
                          {theme.accentColor && (
                            <span className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: theme.accentColor }} />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{theme.name}</span>
                          {theme.isSystem && <Badge>系統</Badge>}
                        </div>
                        {theme.nameEn && <div className="text-xs text-muted">{theme.nameEn}</div>}
                      </button>
                    );
                  })}
                  {/* No theme option */}
                  <button
                    type="button"
                    aria-pressed={!event.themeId}
                    onClick={() => {
                      setEvent({
                        ...event,
                        themeId: undefined,
                        theme: {
                          primaryColor: '#6366F1',
                          primaryDark: '#4F46E5',
                          heroStyle: 'gradient',
                        },
                      });
                    }}
                    className={clsx(
                      'p-3 rounded-lg border text-left transition-colors',
                      !event.themeId
                        ? 'border-primary bg-primary-light/60 ring-[3px] ring-primary/20'
                        : 'border-line hover:bg-ground'
                    )}
                  >
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="w-6 h-6 rounded-full border border-black/10" style={{ backgroundColor: '#6366F1' }} />
                      <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: '#4F46E5' }} />
                    </div>
                    <div className="font-medium text-sm">預設主題</div>
                    <div className="text-xs text-muted">Default</div>
                  </button>
                </div>
                <p className={hint}>決定報名頁面的顏色配置。</p>
              </div>
            </div>
          </section>

          {/* Modules */}
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold leading-none tracking-tight">
                表單模組 <span className="tabular text-muted font-normal">({modules.length})</span>
              </h2>
            </div>

            {/* Add Module Buttons */}
            <div className={clsx(card, 'p-4')}>
              <div className="text-[13px] font-medium text-muted mb-3">新增模組</div>
              <div className="flex flex-wrap gap-2">
                {defaultModules.map((mod) => (
                  <button key={mod.type} type="button" onClick={() => addModule(mod)} className={clsx(btnSecondary, btnSm)}>
                    <IconPlus size={14} />
                    {mod.name}
                  </button>
                ))}
              </div>
            </div>

            {modules.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-line-strong bg-white">
                <EmptyState
                  icon={<IconLayers size={28} />}
                  title="尚未新增模組"
                  hint="從上方選擇模組，組成報名表單。"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {modules.map((module) => {
                  const isOpen = selectedModuleId === module.id;
                  const info = getModuleItemInfo(module);
                  return (
                    <div
                      key={module.id}
                      className={clsx(card, 'overflow-hidden transition-shadow', isOpen && 'ring-[3px] ring-primary/20 border-primary')}
                    >
                      {/* Module Header */}
                      <div
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen}
                        onClick={() => setSelectedModuleId(isOpen ? null : module.id)}
                        onKeyDown={(e) => {
                          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            setSelectedModuleId(isOpen ? null : module.id);
                          }
                        }}
                        className={clsx('px-4 py-3 flex items-center justify-between gap-3 cursor-pointer', isOpen ? 'bg-primary-light/40 border-b border-line-soft' : 'hover:bg-ground')}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <IconLayers size={16} className="text-muted shrink-0" />
                          <input
                            type="text"
                            value={module.name}
                            onChange={(e) => updateModule(module.id, { name: e.target.value })}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="模組名稱"
                            className="font-medium text-sm bg-transparent border border-transparent rounded-md px-1.5 py-0.5 -ml-1.5 min-w-0 flex-1 basis-20 max-w-[12rem] hover:border-line focus:outline-none focus:border-primary focus:bg-white"
                          />
                          <Badge tone="primary" className="hidden sm:inline-flex">{module.type}</Badge>
                          {module.isNew && <Badge tone="warn">未儲存</Badge>}
                          {hasConfiguredSettings(module) && <Badge tone="ok">已設定</Badge>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="tabular text-xs text-muted">{info.count} {info.label}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm('確定要刪除此模組嗎？')) {
                                removeModule(module.id);
                              }
                            }}
                            aria-label={`刪除模組 ${module.name}`}
                            title="刪除模組"
                            className={iconBtnDanger}
                          >
                            <IconTrash size={15} />
                          </button>
                        </div>
                      </div>

                      {/* Module Fields (expanded) */}
                      {isOpen && (
                        <div className="p-4 flex flex-col gap-2">
                          {(module.fields || []).length === 0 && (
                            <div className="text-[13px] text-muted px-1 py-2">此模組還沒有欄位。從下方或右側面板新增。</div>
                          )}
                          {(module.fields || []).map((field: any) => {
                            const ft = fieldTypeOf(field.type);
                            const selected = selectedField?.field?.id === field.id;
                            return (
                              <div
                                key={field.id}
                                role="button"
                                tabIndex={0}
                                aria-pressed={selected}
                                onClick={() => {
                                  setSelectedField({ moduleId: module.id, field });
                                  setActiveTab('properties');
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setSelectedField({ moduleId: module.id, field });
                                    setActiveTab('properties');
                                  }
                                }}
                                className={clsx(
                                  'flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors',
                                  selected ? 'border-primary bg-primary-light/60' : 'border-line hover:bg-ground'
                                )}
                              >
                                <IconGrip size={16} className="text-[#9AA3B0] shrink-0" />
                                <span className="h-7 w-7 rounded-md bg-ground text-muted flex items-center justify-center shrink-0">
                                  {ft ? <ft.Icon size={15} /> : <IconType size={15} />}
                                </span>
                                <div className="flex-1 min-w-0 flex items-center gap-2">
                                  <span className="font-medium text-sm truncate">{field.label}</span>
                                  {field.labelEn && <span className="text-xs text-muted truncate">{field.labelEn}</span>}
                                </div>
                                <Badge>{ft?.label || field.type}</Badge>
                                {field.required && <Badge tone="bad">必填</Badge>}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeField(module.id, field.id);
                                  }}
                                  aria-label={`刪除欄位 ${field.label}`}
                                  title="刪除欄位"
                                  className={iconBtnGhost}
                                >
                                  <IconTrash size={14} />
                                </button>
                              </div>
                            );
                          })}

                          {/* Add Field Button */}
                          <div className="flex flex-wrap items-center gap-1.5 mt-2 pt-3 border-t border-line-soft">
                            {fieldTypes.slice(0, 6).map((ft) => (
                              <button
                                key={ft.type}
                                type="button"
                                onClick={() => addFieldToModule(module.id, ft.type)}
                                className={clsx(btnSecondary, btnSm, '!h-7 !px-2.5 !text-xs')}
                              >
                                <ft.Icon size={13} />
                                {ft.label}
                              </button>
                            ))}
                            <button
                              type="button"
                              onClick={() => setActiveTab('fields')}
                              className="h-7 px-2 text-xs font-medium text-primary hover:underline"
                            >
                              更多欄位類型
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* Right Panel */}
        <aside ref={panelRef} className="w-full lg:w-80 bg-white border-t lg:border-t-0 lg:border-l border-line flex flex-col shrink-0 scroll-mt-[112px] lg:scroll-mt-0">
          {/* Tabs */}
          <div role="tablist" aria-label="編輯面板" className="flex border-b border-line">
            {panelTabs.map((t) => (
              <button
                key={t.key}
                role="tab"
                type="button"
                aria-selected={activeTab === t.key}
                onClick={() => setActiveTab(t.key)}
                className={clsx(
                  'flex-1 h-11 text-sm font-medium border-b-2 -mb-px transition-colors',
                  activeTab === t.key ? 'text-primary border-primary' : 'text-muted border-transparent hover:text-ink'
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Panel Content */}
          <div className="flex-1 lg:overflow-y-auto p-4">
            {activeTab === 'fields' ? (
              <div>
                <div className="text-[13px] font-medium text-muted mb-3">
                  {selectedModuleId
                    ? `加到「${modules.find(m => m.id === selectedModuleId)?.name ?? ''}」`
                    : modules.length > 0 ? `加到「${modules[modules.length - 1].name}」` : '請先新增模組'}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-2 gap-2">
                  {fieldTypes.map((ft) => (
                    <button
                      key={ft.type}
                      type="button"
                      onClick={() => {
                        if (modules.length === 0) {
                          alert('請先新增模組');
                          return;
                        }
                        const targetModule = selectedModuleId
                          ? modules.find(m => m.id === selectedModuleId)
                          : modules[modules.length - 1];
                        if (targetModule) {
                          addFieldToModule(targetModule.id, ft.type);
                        }
                      }}
                      className="flex flex-col items-center gap-2 p-3 rounded-lg border border-line hover:border-primary hover:bg-primary-light/40 transition-colors"
                    >
                      <ft.Icon size={20} className="text-muted" />
                      <span className="text-xs font-medium">{ft.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : selectedField ? (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  {(() => { const ft = fieldTypeOf(selectedField.field.type); return ft ? <ft.Icon size={16} className="text-muted" /> : null; })()}
                  <span className="text-[13px] font-medium text-muted">{fieldTypeOf(selectedField.field.type)?.label || selectedField.field.type} 欄位</span>
                </div>

                <div>
                  <label htmlFor="fieldId" className={label}>欄位 ID</label>
                  <input
                    id="fieldId"
                    type="text"
                    value={selectedField.field.id}
                    onChange={(e) => {
                      const newId = e.target.value.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();
                      // Update the field ID in the module
                      setModules(modules.map((m) =>
                        m.id === selectedField.moduleId
                          ? {
                              ...m,
                              fields: (m.fields || []).map((f: any) =>
                                f.id === selectedField.field.id ? { ...f, id: newId } : f
                              ),
                            }
                          : m
                      ));
                      setSelectedField({
                        ...selectedField,
                        field: { ...selectedField.field, id: newId },
                      });
                    }}
                    className={clsx(input, 'font-mono')}
                    placeholder="例: name, email, phone"
                  />
                  <p className={hint}>表單資料的鍵值。常用：name、email、phone、birthday、gender、taiwan_id、emergency_contact、emergency_phone、address、remit_date、remit_bank、remit_last5、remit_amount。</p>
                </div>

                <div>
                  <label htmlFor="fieldLabel" className={label}>標籤（中文）</label>
                  <input
                    id="fieldLabel"
                    type="text"
                    value={selectedField.field.label}
                    onChange={(e) => updateField(selectedField.moduleId, selectedField.field.id, { label: e.target.value })}
                    className={input}
                  />
                </div>

                <div>
                  <label htmlFor="fieldLabelEn" className={label}>標籤（英文）</label>
                  <input
                    id="fieldLabelEn"
                    type="text"
                    value={selectedField.field.labelEn || ''}
                    onChange={(e) => updateField(selectedField.moduleId, selectedField.field.id, { labelEn: e.target.value })}
                    className={input}
                  />
                </div>

                <div>
                  <label htmlFor="fieldPlaceholder" className={label}>預設提示</label>
                  <input
                    id="fieldPlaceholder"
                    type="text"
                    value={selectedField.field.placeholder || ''}
                    onChange={(e) => updateField(selectedField.moduleId, selectedField.field.id, { placeholder: e.target.value })}
                    className={input}
                  />
                </div>

                <div>
                  <label htmlFor="fieldHint" className={label}>說明文字</label>
                  <input
                    id="fieldHint"
                    type="text"
                    value={selectedField.field.hint || ''}
                    onChange={(e) => updateField(selectedField.moduleId, selectedField.field.id, { hint: e.target.value })}
                    className={input}
                  />
                </div>

                <Toggle
                  label="必填欄位"
                  checked={!!selectedField.field.required}
                  onChange={(v) => updateField(selectedField.moduleId, selectedField.field.id, { required: v })}
                />

                {/* Options for select/radio */}
                {['select', 'radio', 'image_radio'].includes(selectedField.field.type) && (
                  <div>
                    <span className={label}>選項</span>
                    <div className="flex flex-col gap-2">
                      {(selectedField.field.options || []).map((opt: any, idx: number) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="text"
                            value={opt.label}
                            aria-label={`選項 ${idx + 1}`}
                            onChange={(e) => {
                              const newOptions = [...selectedField.field.options];
                              newOptions[idx] = { ...opt, label: e.target.value, value: e.target.value };
                              updateField(selectedField.moduleId, selectedField.field.id, { options: newOptions });
                            }}
                            className={input}
                            placeholder="選項標籤"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newOptions = selectedField.field.options.filter((_: any, i: number) => i !== idx);
                              updateField(selectedField.moduleId, selectedField.field.id, { options: newOptions });
                            }}
                            aria-label={`移除選項 ${idx + 1}`}
                            className={clsx(iconBtnDanger, '!h-9 !w-9 shrink-0')}
                          >
                            <IconX size={15} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newOptions = [
                            ...(selectedField.field.options || []),
                            { value: `opt${Date.now()}`, label: '新選項' },
                          ];
                          updateField(selectedField.moduleId, selectedField.field.id, { options: newOptions });
                        }}
                        className="h-9 w-full rounded-md border border-dashed border-line-strong text-sm text-muted hover:border-primary hover:text-primary inline-flex items-center justify-center gap-1.5"
                      >
                        <IconPlus size={14} />
                        新增選項
                      </button>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-line-soft">
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('確定要刪除此欄位嗎？')) {
                        removeField(selectedField.moduleId, selectedField.field.id);
                      }
                    }}
                    className={clsx(btnDanger, 'w-full')}
                  >
                    <IconTrash size={16} />
                    刪除此欄位
                  </button>
                </div>
              </div>
            ) : activeTab === 'settings' ? (
              // Module Settings Tab
              (() => {
                const selectedModule = modules.find(m => m.id === selectedModuleId);
                if (!selectedModule) {
                  return (
                    <EmptyState
                      icon={<IconSettings size={26} />}
                      title="請先選擇一個模組"
                      hint="點選左側模組標題後，這裡會顯示它的設定。"
                      className="py-8"
                    />
                  );
                }

                const updateModuleSettings = (newSettings: any) => {
                  updateModule(selectedModule.id, { settings: newSettings });
                };

                const sectionTitle = (text: string) => (
                  <div className="text-[13px] font-medium text-muted mb-3">{text}</div>
                );

                // Render settings editor content based on module type
                const renderSettingsEditor = () => {
                  switch (selectedModule.type) {
                    case 'race_selection':
                      return (
                        <>
                          {sectionTitle('組別選擇設定')}
                          <RaceSelectionSettings settings={selectedModule.settings || {}} onChange={updateModuleSettings} />
                        </>
                      );
                    case 'payment':
                      return (
                        <>
                          {sectionTitle('付款資訊設定')}
                          <PaymentSettings settings={selectedModule.settings || {}} onChange={updateModuleSettings} />
                        </>
                      );
                    case 'agreement':
                      return (
                        <>
                          {sectionTitle('報名須知設定')}
                          <AgreementSettings settings={selectedModule.settings || {}} onChange={updateModuleSettings} />
                        </>
                      );
                    case 'shirt_selection':
                      return (
                        <>
                          {sectionTitle('紀念衣選擇設定')}
                          <ShirtSelectionSettings settings={selectedModule.settings || {}} onChange={updateModuleSettings} />
                        </>
                      );
                    case 'merchandise':
                      return (
                        <>
                          {sectionTitle('商品加購設定')}
                          <MerchandiseSettings settings={selectedModule.settings || {}} onChange={updateModuleSettings} />
                        </>
                      );
                    case 'shipping':
                      return (
                        <>
                          {sectionTitle('寄送資訊設定')}
                          <ShippingSettings settings={selectedModule.settings || {}} onChange={updateModuleSettings} />
                        </>
                      );
                    default:
                      return (
                        <EmptyState
                          icon={<IconLayers size={26} />}
                          title="此模組類型沒有額外設定"
                          hint={`${selectedModule.name} (${selectedModule.type})`}
                          className="py-8"
                        />
                      );
                  }
                };

                return (
                  <div>
                    {renderSettingsEditor()}
                    {/* JSON Editor Button */}
                    <div className="mt-6 pt-4 border-t border-line-soft">
                      <button type="button" onClick={() => setJsonEditorOpen(true)} className={clsx(btnSecondary, 'w-full')}>
                        <IconCode size={16} />
                        編輯 JSON
                      </button>
                      <p className="text-xs text-muted text-center mt-1.5">進階設定</p>
                    </div>
                  </div>
                );
              })()
            ) : (
              <EmptyState
                icon={<IconPointer size={26} />}
                title="尚未選擇欄位"
                hint="展開左側模組，點選一個欄位來編輯它的屬性。"
                className="py-8"
              />
            )}
          </div>
        </aside>
      </div>

      {/* JSON Settings Editor Modal */}
      {(() => {
        const selectedModule = modules.find(m => m.id === selectedModuleId);
        if (!selectedModule) return null;
        return (
          <JsonSettingsEditor
            isOpen={jsonEditorOpen}
            onClose={() => setJsonEditorOpen(false)}
            settings={selectedModule.settings || {}}
            moduleName={selectedModule.name}
            moduleType={selectedModule.type}
            onSave={(newSettings) => {
              updateModule(selectedModule.id, { settings: newSettings });
            }}
          />
        );
      })()}
    </div>
  );
}
